import {
  convertDateToYYYYMMDDFormat,
  CustomDateRangePicker,
  getFirstDayOfMonth,
  getToday,
  NoDataComponent,
  PageWrapper,
  STATUSPAGE,
  TableVirtualized,
  TableWithGroupingsLoadingSkeleton,
} from '@repo/ui';
import { HandleValidatedByProfessionalsUseCaseInterface } from 'domain/usecases';
import { TYPE_REPORT } from 'domain/value-objects/type-report';
import { useFormik } from 'formik';
import { ReportsGeneralFilters } from 'presentation/app/modules/Reports/components/reports-general-filters';
import { useValidatedByProfessionals } from 'presentation/app/modules/Reports/hooks/useValidatedByProfessionals';
import { useAppSelector } from 'presentation/config/hooks/useRedux';
import { Col, Row, Card, Label, Button, Spinner } from 'reactstrap';
import { v4 as uuid } from 'uuid';
import * as Yup from 'yup';

import { makeActivitiesValidatedByProfessionalsColumns } from '../../../common/columns/activities/activities-validated-by-professionals-columns';

const ValidatedByProfessionals = () => {
  const { validatedByProfessionals } = useAppSelector(
    (state) => state.validatedByProfessionals,
  );

  const {
    loading,
    hasFetchedData,
    selectedSubmitBtn,
    setSelectedSubmitBtn,
    requestHandleValidatedByProfessionals,
    requestHandleValidatedByProfessionalsJson,
  } = useValidatedByProfessionals();

  const validation = useFormik({
    enableReinitialize: true,

    initialValues: {
      unit: null,
      groups: [],
      entity: null,
      course: null,
      semester: null,
      ends: convertDateToYYYYMMDDFormat(getToday()),
      starts: convertDateToYYYYMMDDFormat(getFirstDayOfMonth()),
    },

    validationSchema: Yup.object({
      entity: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
      course: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
      semester: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
      unit: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
      groups: Yup.array().of(
        Yup.object()
          .shape({
            label: Yup.string(),
            value: Yup.string(),
          })
          .nullable(),
      ),
      starts: Yup.date().required('Campo é obrigatório!'),
      ends: Yup.date().required('Campo é obrigatório!'),
    }),

    onSubmit: (values) => {
      const valuesToSubmit: Omit<
        HandleValidatedByProfessionalsUseCaseInterface.Params,
        'type'
      > = {
        entity: values.entity?.value,
        course: values.course?.value,
        semester: values.semester?.value,
        curricularUnit: values.unit?.value,
        groups: values.groups.map((group) => group.value),
        ends: values.ends,
        starts: values.starts,
      };

      switch (selectedSubmitBtn) {
        case 'export-pdf':
          requestHandleValidatedByProfessionals({
            ...valuesToSubmit,
            type: TYPE_REPORT.PDF,
          });
          break;
        case 'export-excel':
          requestHandleValidatedByProfessionals({
            ...valuesToSubmit,
            type: TYPE_REPORT.EXCEL,
          });
          break;
        case 'export-json':
          requestHandleValidatedByProfessionalsJson({
            ...valuesToSubmit,
            type: TYPE_REPORT.JSON,
          });
          break;
        default:
          break;
      }
    },
  });

  const formattedData = validatedByProfessionals?.reduce(
    (acc, professional) => [
      ...acc,
      {
        id: uuid(),
        isMacroHeader: true,
        title: (
          <div className="d-flex gap-3 align-items-center">
            <span>{professional.professional}</span>
            <span className="fs-6">
              Total de atividades do Profissional: {professional.total}
            </span>
          </div>
        ),
      },
      ...professional.groups.flatMap((group) => [
        {
          id: uuid(),
          isHeader: true,
          title: (
            <div className="d-flex justify-content-between">
              <span>{group.group}</span>{' '}
              <span>Total de atividades do Grupo: {group.total}</span>
            </div>
          ),
        },
        ...group.activities.map((activity) => ({
          id: uuid(),
          ...activity,
        })),
      ]),
    ],
    [],
  );

  return (
    <PageWrapper title={`Atividades Validadas por Profissionais`}>
      <h4 className="mb-4">Atividades Validadas por Profissionais</h4>
      <Card>
        <ReportsGeneralFilters
          validation={validation}
          inputsChildren={
            <>
              <Row>
                <Col sm={12} md={6} lg={4} className="mb-3">
                  <Row>
                    <Label htmlFor="initial-date" className="form-label">
                      Período
                    </Label>
                    <CustomDateRangePicker
                      initialStartDate={getFirstDayOfMonth()}
                      initialEndDate={getToday()}
                      placement="auto"
                      onDateChange={(start, ends) => {
                        validation.setFieldValue(
                          'starts',
                          convertDateToYYYYMMDDFormat(start),
                        );
                        validation.setFieldValue(
                          'ends',
                          convertDateToYYYYMMDDFormat(ends),
                        );
                      }}
                    />
                  </Row>
                </Col>
              </Row>
            </>
          }
          buttonsChildren={
            <div className="d-flex gap-2 justify-content-end align-content-end">
              <Button
                onClick={() => {
                  setSelectedSubmitBtn('export-json');
                }}
                color="primary"
                type="submit"
                id="export-json"
                disabled={Boolean(loading)}
              >
                {selectedSubmitBtn === 'export-json' && Boolean(loading) ? (
                  <Spinner size="sm">{['Carregando...']}</Spinner>
                ) : (
                  <>
                    Visualizar
                    <i className="ri-computer-line mx-2"></i>
                  </>
                )}
              </Button>
              <Button
                onClick={() => {
                  setSelectedSubmitBtn('export-pdf');
                }}
                color="primary"
                type="submit"
                className="btn btn-light waves-effect waves-light"
                id={'save_button'}
                disabled={Boolean(loading)}
              >
                {selectedSubmitBtn === 'export-pdf' && Boolean(loading) ? (
                  <Spinner size="sm">{['Carregando...']}</Spinner>
                ) : (
                  <>
                    Exportar para PDF
                    <i className="las la-file-pdf mx-2"></i>
                  </>
                )}
              </Button>
              <Button
                onClick={() => {
                  setSelectedSubmitBtn('export-excel');
                }}
                color="primary"
                type="submit"
                id={'save_button'}
                className="btn btn-light waves-effect waves-light"
                disabled={Boolean(loading)}
              >
                {selectedSubmitBtn === 'export-excel' && Boolean(loading) ? (
                  <Spinner size="sm">{['Carregando...']}</Spinner>
                ) : (
                  <>
                    Exportar para Excel
                    <i className="ms-2 ri-file-excel-2-line"></i>
                  </>
                )}
              </Button>
            </div>
          }
        />
        {validatedByProfessionals?.length === 0 &&
          hasFetchedData &&
          loading === STATUSPAGE.DONE && (
            <div className="p-4">
              <NoDataComponent />
            </div>
          )}

        {loading === STATUSPAGE.LOADING && (
          <TableWithGroupingsLoadingSkeleton />
        )}

        {loading === STATUSPAGE.DONE &&
          validatedByProfessionals?.length > 0 && (
            <TableVirtualized
              columns={makeActivitiesValidatedByProfessionalsColumns()}
              data={formattedData}
            />
          )}
      </Card>
    </PageWrapper>
  );
};

export default ValidatedByProfessionals;
